import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import { usePreferences } from './usePreferences';
import { unitName } from './units';

/**
 * Choose the unit loads are entered and displayed in.
 *
 * Nothing about the stored data changes — the server keeps kilograms either way
 * (see units.js). This only decides how numbers are shown and how a typed
 * number is interpreted, which is why switching it is safe at any time and why
 * the copy says so.
 */
const UnitPreference = () => {
  const { token } = useAuth();
  const { unit, availableUnits, loaded } = usePreferences();

  // Held locally so the control responds immediately; the fetched value is the
  // source of truth until the first change.
  const [pending, setPending] = useState(null);
  const [status, setStatus] = useState(null);

  const current = pending ?? unit;

  const choose = async (next) => {
    if (next === current) return;

    const previous = current;
    setPending(next);
    setStatus(null);

    try {
      await axios.put(
        buildApiUrl('/preferences'),
        { unit: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus({ tone: 'success', message: `Loads now shown in ${unitName(next)}.` });
    } catch (error) {
      // Roll the control back rather than leaving it showing a unit the server
      // never accepted — the previous screen-level pattern here was to report
      // success unconditionally, which is how the save path hid its failures.
      setPending(previous);
      setStatus({
        tone: 'error',
        message: error?.response?.data?.error || 'Could not save that. Your unit is unchanged.',
      });
    }
  };

  return (
    <div className="unit-preference">
      <div
        className="unit-preference-options"
        role="group"
        aria-label="Load units"
      >
        {availableUnits.map((option) => (
          <button
            key={option}
            type="button"
            className={option === current ? 'btn-primary' : 'btn-ghost'}
            aria-pressed={option === current}
            disabled={!loaded}
            onClick={() => choose(option)}
          >
            {unitName(option)}
          </button>
        ))}
      </div>

      {status && (
        <p className={status.tone === 'error' ? 'status-error' : 'status-success'} role="status">
          {status.message}
        </p>
      )}
    </div>
  );
};

export default UnitPreference;
